import { ActionIcon, View } from '@/components'
import { ClimateEngagementScore, ClimateEngagementScoreProps } from './ClimateEngagementScore'
import { APIClient } from '@/services'
import { ScoreTopic } from '@/types'
import { Theme, variantProvider } from '@/app'
import { motion, MotionValue, useMotionValueEvent, useScroll, useTransform } from 'framer-motion'
import { useEffect, useRef, useState } from 'react'
import { useIsMobile } from '@/utils'
import { Organisation } from '@/features'

type PerTopicEngagementScoreProps = Omit<ClimateEngagementScoreProps, 'topic' | 'onExpand'> & {
  industry?: Organisation.T.OrganisationCategory
  onExpand: (topic: ScoreTopic) => void
  defaultTopicValue?: string
}

type DotProps = {
  index: number
  progress?: MotionValue<number>
  onPress: () => void
}

const Dot = (props: DotProps) => {
  const { index, progress, onPress, ...rest } = props
  const inRange = [index - 1, index, index + 1]

  const scale = useTransform(progress, inRange, [0.66, 1, 0.66], {
    clamp: true
  })

  const backgroundColor = useTransform(
    progress,
    inRange,
    [Theme.colors.light.neutral3, Theme.colors.light.primary3, Theme.colors.light.neutral3],
    {
      clamp: true
    }
  )

  return (
    <motion.div
      onClick={onPress}
      style={{
        ...styles.dot,
        scale,
        backgroundColor
      }}
    />
  )
}

function useMotionValueState<T>(value: MotionValue<T>) {
  const [state, setState] = useState(() => {
    return value.get()
  })

  useMotionValueEvent(value, 'change', v => {
    setState(v)
  })

  return state
}

export const PerTopicEngagementScore = (props: PerTopicEngagementScoreProps) => {
  const { organisation, industry, onExpand, defaultTopicValue, ...otherProps } = props

  const topics = APIClient.Organisations.useScoreTopics()

  const topicScores = APIClient.Organisations.useTopicScores({
    topics: topics.data.map(t => t.value),
    category: industry?.slug,
    organisation: organisation?.id
  })

  const renderItem = (item: ReturnType<typeof APIClient.Organisations.useTopicScores>[number], index: number) => {
    const score = item.data?.score
    const topic = topics?.data?.[index]

    return (
      <View style={{ minWidth: '100%', scrollSnapAlign: 'center' }} key={topic?.value}>
        <ClimateEngagementScore
          score={score}
          variant="modal"
          showReviews={false}
          loading={item.isLoading}
          {...otherProps}
          organisation={organisation}
          topic={topic}
          onExpand={() => {
            onExpand(topic)
          }}
        />
      </View>
    )
  }

  const scrollRef = useRef<HTMLDivElement>()

  const { scrollXProgress } = useScroll({
    container: scrollRef
  })

  const scrollIndex = useTransform(scrollXProgress, [0, 1], [0, topics?.data.length - 1], {
    clamp: true
  })

  function scrollTo(index: number) {
    const scrollerWidth = scrollRef.current.getBoundingClientRect().width
    scrollRef.current.scrollTo({
      left: scrollerWidth * index,
      behavior: 'smooth'
    })
  }

  const onScrollToItem = (index: number) => {
    return () => {
      scrollTo(index)
    }
  }

  const showScrollLeft = useMotionValueState(useTransform(scrollIndex, v => Math.round(v) > 0))
  const showScrollRight = useMotionValueState(useTransform(scrollIndex, v => Math.round(v) < topics?.data?.length - 1))

  const isMobile = useIsMobile()

  // Tracks the desired carousel page for every snap callback.
  // Set once on first topics load (to defaultTopicValue if provided),
  // then kept in sync as the user manually scrolls.
  const currentIndexRef = useRef(0)
  const hasSetInitialRef = useRef(false)

  useEffect(() => {
    const topicCount = topics?.data?.length ?? 0
    if (topicCount === 0) return

    // Initialise to the defaultTopicValue index on first load.
    // Runs synchronously in the effect body so both the ResizeObserver
    // initial callback and the rAF below read the correct index.
    if (!hasSetInitialRef.current) {
      if (defaultTopicValue) {
        const found = topics.data.findIndex(t => t.value === defaultTopicValue)
        if (found > 0) currentIndexRef.current = found
      }
      hasSetInitialRef.current = true
    }

    const snapToCurrentPage = () => {
      const el = scrollRef.current
      if (!el) return
      const idx = Math.min(Math.max(0, currentIndexRef.current), topicCount - 1)
      const w = el.getBoundingClientRect().width
      if (w <= 0) return
      el.scrollTo({ left: w * idx, behavior: 'auto' })
    }

    // Keep currentIndexRef in sync when the user manually scrolls.
    const unsubscribeScroll = scrollIndex.on('change', v => {
      currentIndexRef.current = Math.round(Math.min(Math.max(0, v), topicCount - 1))
    })

    window.addEventListener('resize', snapToCurrentPage)
    const vv = window.visualViewport
    vv?.addEventListener('resize', snapToCurrentPage)

    const el = scrollRef.current
    const ro = el && typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => snapToCurrentPage()) : null
    ro?.observe(el)

    requestAnimationFrame(snapToCurrentPage)

    return () => {
      unsubscribeScroll()
      window.removeEventListener('resize', snapToCurrentPage)
      vv?.removeEventListener('resize', snapToCurrentPage)
      ro?.disconnect()
    }
  }, [scrollIndex, topics?.data?.length])

  return (
    <View variants={['column', 'gap:3']} style={{ maxWidth: '100%' }}>
      <motion.div css={styles.scroller} ref={scrollRef}>
        {topicScores.map(renderItem)}
      </motion.div>
      <View variants={['row', 'justifySpaceBetween', 'alignCenter']}>
        {isMobile ? null : (
          <ActionIcon
            debugName="Previous score"
            icon="chevron-left"
            disabled={!showScrollLeft}
            variants={['minimal', 'neutral4', !showScrollLeft && 'hidden']}
            onPress={() => {
              scrollTo(Math.round(scrollIndex.get()) - 1)
            }}
          />
        )}

        <View variants={['row', 'justifyCenter', 'gap:1', 'flex']}>
          {topics?.data?.map((_, index) => (
            <Dot key={index} progress={scrollIndex} index={index} onPress={onScrollToItem(index)} />
          ))}
        </View>
        {isMobile ? null : (
          <ActionIcon
            debugName="Previous score"
            icon="chevron-right"
            variants={['minimal', 'neutral4', !showScrollRight && 'hidden']}
            onPress={() => {
              scrollTo(Math.round(scrollIndex.get()) + 1)
            }}
            disabled={!showScrollRight}
          />
        )}
      </View>
    </View>
  )
}

const styles = variantProvider.createComponentStyle(
  theme => ({
    scroller: {
      ...theme.spacing.gap(2),
      display: 'flex',
      flexDirection: 'row',
      overflowX: 'scroll',
      overflowY: 'hidden',
      maxWidth: '100%',
      scrollSnapType: 'x mandatory',
      scrollbarWidth: 'none',
      '&::-webkit-scrollbar': {
        display: 'none'
      }
    },
    dot: {
      width: 12,
      borderRadius: theme.borderRadius.rounded,
      height: 12
    }
  }),
  true
)
